/**
 * FamilyVault Component (My Family Should Know)
 * A comprehensive financial data vault for clients to store important financial information
 */
import React, { useState, useEffect } from 'react';
import {
  FaShieldAlt, FaHeartbeat, FaCar, FaPiggyBank, FaUniversity, FaMailBulk,
  FaChartLine, FaChartPie, FaFileAlt, FaHandHoldingUsd, FaScroll, FaPlus,
  FaEdit, FaTrash, FaTimes, FaCheck, FaSpinner, FaSave, FaUsers,
  FaFolder, FaExclamationTriangle, FaChevronDown, FaChevronUp, FaDownload,
  FaEnvelope, FaUserFriends, FaHandshake, FaBriefcase, FaBalanceScale, FaAddressCard
} from 'react-icons/fa';
import axios from 'axios';
import jsPDF from 'jspdf';
import { useAuth } from '../contexts/AuthContext';
import ShareFamilyVaultModal from '../components/family-vault/ShareFamilyVaultModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { id: 'life_insurance', label: 'Life Insurance', icon: FaShieldAlt, color: 'blue' },
  { id: 'health_insurance', label: 'Health Insurance', icon: FaHeartbeat, color: 'red' },
  { id: 'motor_insurance', label: 'Motor Insurance', icon: FaCar, color: 'green' },
  { id: 'bank_savings', label: 'Bank Savings Accounts', icon: FaPiggyBank, color: 'emerald' },
  { id: 'bank_fd', label: 'Bank Fixed Deposits', icon: FaUniversity, color: 'purple' },
  { id: 'post_office', label: 'Post Office Schemes', icon: FaMailBulk, color: 'orange' },
  { id: 'shares', label: 'Shares', icon: FaChartLine, color: 'cyan' },
  { id: 'mf_sip', label: 'Mutual Fund SIP', icon: FaChartPie, color: 'indigo' },
  { id: 'mf_lumpsum', label: 'Mutual Fund Lumpsum', icon: FaChartPie, color: 'violet' },
  { id: 'document_locations', label: 'Document Locations', icon: FaFolder, color: 'yellow' },
  { id: 'loans', label: 'My Loans', icon: FaHandHoldingUsd, color: 'rose' },
  { id: 'will', label: 'My Will', icon: FaScroll, color: 'amber' },
  { id: 'anything_else', label: 'Anything Else', icon: FaFileAlt, color: 'gray' },
  { id: 'nominees', label: 'Nominees', icon: FaUsers, color: 'teal' }
];

const FIELD_CONFIGS = {
  life_insurance: [
    { name: 'policy_number', label: 'Policy Number', type: 'text', required: true },
    { name: 'company', label: 'Company', type: 'text', required: true },
    { name: 'policy_type', label: 'Policy Type', type: 'text' },
    { name: 'cover_amount', label: 'Life Insurance Cover (₹)', type: 'number' },
    { name: 'maturity_date', label: 'Maturity Date', type: 'date' },
    { name: 'status', label: 'Status', type: 'select', options: ['Live', 'Lapsed'] }
  ],
  health_insurance: [
    { name: 'policy_number', label: 'Policy Number', type: 'text', required: true },
    { name: 'company', label: 'Company', type: 'text', required: true },
    { name: 'policy_type', label: 'Policy Type', type: 'text' },
    { name: 'cover_amount', label: 'Insurance Cover (₹)', type: 'number' },
    { name: 'renewal_date', label: 'Renewal Date', type: 'date' },
    { name: 'status', label: 'Status', type: 'select', options: ['Live', 'Lapsed'] }
  ],
  motor_insurance: [
    { name: 'policy_number', label: 'Policy Number', type: 'text', required: true },
    { name: 'company', label: 'Company', type: 'text', required: true },
    { name: 'policy_type', label: 'Policy Type', type: 'text' },
    { name: 'cover_amount', label: 'Insurance Cover (₹)', type: 'number' },
    { name: 'renewal_date', label: 'Renewal Date', type: 'date' },
    { name: 'status', label: 'Status', type: 'select', options: ['Live', 'Lapsed'] }
  ],
  bank_savings: [
    { name: 'account_number', label: 'Account Number', type: 'text', required: true },
    { name: 'bank', label: 'Bank Name', type: 'text', required: true },
    { name: 'branch', label: 'Branch', type: 'text' }
  ],
  bank_fd: [
    { name: 'amount', label: 'FD Amount (₹)', type: 'number', required: true },
    { name: 'account_number', label: 'Account Number', type: 'text', required: true },
    { name: 'bank', label: 'Bank Name', type: 'text', required: true },
    { name: 'branch', label: 'Branch', type: 'text' },
    { name: 'maturity_date', label: 'Maturity Date', type: 'date' }
  ],
  post_office: [
    { name: 'scheme_name', label: 'Scheme Name', type: 'text', required: true },
    { name: 'account_number', label: 'Account Number', type: 'text', required: true },
    { name: 'branch', label: 'Branch', type: 'text' },
    { name: 'maturity_date', label: 'Maturity Date', type: 'date' }
  ],
  shares: [
    { name: 'company_name', label: 'Company Name', type: 'text', required: true },
    { name: 'total_shares', label: 'Total Shares', type: 'number', required: true },
    { name: 'dmat_company', label: 'DMAT Account Company', type: 'text' },
    { name: 'dmat_number', label: 'DMAT Account Number', type: 'text' }
  ],
  mf_sip: [
    { name: 'company_name', label: 'AMC Name', type: 'text', required: true },
    { name: 'scheme', label: 'Scheme Name', type: 'text', required: true },
    { name: 'term', label: 'Term', type: 'text' },
    { name: 'maturity_date', label: 'Maturity Date', type: 'date' },
    { name: 'mapped_goal', label: 'Mapped to Goal', type: 'text' }
  ],
  mf_lumpsum: [
    { name: 'company_name', label: 'AMC Name', type: 'text', required: true },
    { name: 'scheme', label: 'Scheme Name', type: 'text', required: true },
    { name: 'term', label: 'Term', type: 'text' },
    { name: 'maturity_date', label: 'Maturity Date', type: 'date' },
    { name: 'mapped_goal', label: 'Mapped to Goal', type: 'text' }
  ],
  loans: [
    { name: 'loan_type', label: 'Loan Type', type: 'select', options: ['Home Loan', 'Car Loan', 'Personal Loan', 'Education Loan', 'Business Loan', 'Gold Loan', 'Other'], required: true },
    { name: 'company', label: 'Lender/Company', type: 'text', required: true },
    { name: 'account_number', label: 'Account Number', type: 'text' },
    { name: 'term', label: 'Term', type: 'text' },
    { name: 'outstanding_amount', label: 'Outstanding Amount (₹)', type: 'number' },
    { name: 'closing_date', label: 'Closing Date', type: 'date' }
  ],
  nominees: [
    { name: 'name', label: 'Nominee Name', type: 'text', required: true },
    { name: 'contact', label: 'Contact Number', type: 'tel', required: true },
    { name: 'email', label: 'Email Address', type: 'email' }
  ]
};

const FamilyVault = () => {
  const { user } = useAuth();
  const [vaultData, setVaultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [addingTo, setAddingTo] = useState(null);
  const [formData, setFormData] = useState({});
  const [message, setMessage] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const getToken = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  useEffect(() => {
    fetchVaultData();
  }, []);

  const fetchVaultData = async () => {
    try {
      const response = await axios.get(`${API}/client/family-vault`, { headers: headers() });
      setVaultData(response.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load vault data' });
    } finally {
      setLoading(false);
    }
  };

  // PDF Generation Function
  //   mode = 'download' → triggers browser download (default legacy behaviour)
  //   mode = 'share'    → returns { fileName, base64 } for uploading to backend
  const generatePDF = async (mode = 'download') => {
    if (!vaultData) return null;

    if (mode === 'download') setGeneratingPDF(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // Helper functions
      const addText = (text, x, y, options = {}) => {
        const { fontSize = 10, fontStyle = 'normal', color = [0, 0, 0], maxWidth = null } = options;
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', fontStyle);
        pdf.setTextColor(...color);
        if (maxWidth) {
          pdf.text(String(text || ''), x, y, { maxWidth });
        } else {
          pdf.text(String(text || ''), x, y);
        }
      };

      const addLine = (y, color = [45, 90, 143]) => {
        pdf.setDrawColor(...color);
        pdf.line(margin, y, pageWidth - margin, y);
      };

      const checkPageBreak = (requiredSpace) => {
        if (yPos + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      const formatCurrencyPDF = (amount) => {
        if (!amount) return '-';
        return `Rs. ${Number(amount).toLocaleString('en-IN')}`;
      };

      const formatDatePDF = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
      };

      // Header with branding
      pdf.setFillColor(15, 23, 42);
      pdf.rect(0, 0, pageWidth, 50, 'F');
      
      // Gold accent line
      pdf.setFillColor(255, 215, 0);
      pdf.rect(0, 48, pageWidth, 2, 'F');
      
      addText('MY FAMILY SHOULD KNOW', margin, 15, { fontSize: 22, fontStyle: 'bold', color: [255, 215, 0] });
      addText('Financial Information Vault', margin, 23, { fontSize: 12, color: [200, 200, 200] });
      
      // Client Name and Email
      const clientName = user?.name || 'Client';
      const clientEmail = user?.email || '';
      addText(`Name: ${clientName}`, margin, 33, { fontSize: 11, fontStyle: 'bold', color: [255, 255, 255] });
      if (clientEmail) {
        addText(`Email: ${clientEmail}`, margin, 40, { fontSize: 10, color: [200, 200, 200] });
      }
      
      addText('CONFIDENTIAL DOCUMENT', margin, 46, { fontSize: 8, color: [255, 100, 100] });
      
      const reportDate = new Date().toLocaleDateString('en-IN', { 
        day: '2-digit', month: 'long', year: 'numeric' 
      });
      addText(`Generated: ${reportDate}`, pageWidth - margin - 45, 15, { fontSize: 9, color: [150, 150, 150] });
      addText('Arth Gurukul', pageWidth - margin - 45, 23, { fontSize: 9, color: [255, 215, 0] });
      
      yPos = 60;

      // Nominees Section (First - Most Important)
      const nominees = vaultData.nominees || [];
      if (nominees.length > 0) {
        pdf.setFillColor(26, 41, 66);
        pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, 8 + (nominees.length * 18), 3, 3, 'F');
        
        addText('NOMINEES (Emergency Contacts)', margin + 5, yPos + 6, { fontSize: 12, fontStyle: 'bold', color: [255, 215, 0] });
        
        let nomineeY = yPos + 14;
        nominees.forEach((nominee, idx) => {
          addText(`${idx + 1}. ${nominee.name || '-'}`, margin + 8, nomineeY, { fontSize: 10, fontStyle: 'bold', color: [255, 255, 255] });
          addText(`Contact: ${nominee.contact || '-'}`, margin + 60, nomineeY, { fontSize: 9, color: [200, 200, 200] });
          addText(`Email: ${nominee.email || '-'}`, margin + 110, nomineeY, { fontSize: 9, color: [200, 200, 200] });
          nomineeY += 18;
        });
        
        yPos = nomineeY + 5;
      }

      // Section renderer helper
      const renderPDFSection = (title, items, fields, iconColor) => {
        if (!items || items.length === 0) return;
        
        checkPageBreak(30 + items.length * 25);
        
        // Section header
        pdf.setFillColor(...iconColor);
        pdf.roundedRect(margin, yPos, 4, 8, 1, 1, 'F');
        addText(title.toUpperCase(), margin + 8, yPos + 6, { fontSize: 11, fontStyle: 'bold', color: [30, 58, 95] });
        addLine(yPos + 10, [200, 200, 200]);
        yPos += 15;
        
        items.forEach((item, idx) => {
          checkPageBreak(25);
          
          // Item background
          pdf.setFillColor(248, 250, 252);
          pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, 20, 2, 2, 'F');
          
          let xPos = margin + 5;
          const fieldWidth = (pageWidth - 2 * margin - 10) / Math.min(fields.length, 4);
          
          fields.slice(0, 4).forEach((field, fIdx) => {
            const value = item[field.name];
            let displayValue = value || '-';
            
            if (field.type === 'number' && (field.name.includes('amount') || field.name.includes('cover'))) {
              displayValue = formatCurrencyPDF(value);
            } else if (field.type === 'date') {
              displayValue = formatDatePDF(value);
            }
            
            addText(field.label, xPos, yPos + 6, { fontSize: 7, color: [100, 100, 100] });
            addText(String(displayValue), xPos, yPos + 12, { fontSize: 9, fontStyle: 'bold', color: [30, 30, 30] });
            xPos += fieldWidth;
          });
          
          yPos += 23;
        });
        
        yPos += 5;
      };

      // Life Insurance
      renderPDFSection('Life Insurance', vaultData.life_insurance, FIELD_CONFIGS.life_insurance, [59, 130, 246]);
      
      // Health Insurance
      renderPDFSection('Health Insurance', vaultData.health_insurance, FIELD_CONFIGS.health_insurance, [239, 68, 68]);
      
      // Motor Insurance
      renderPDFSection('Motor Insurance', vaultData.motor_insurance, FIELD_CONFIGS.motor_insurance, [34, 197, 94]);
      
      // Bank Savings
      renderPDFSection('Bank Savings Accounts', vaultData.bank_savings, FIELD_CONFIGS.bank_savings, [16, 185, 129]);
      
      // Bank FD
      renderPDFSection('Fixed Deposits', vaultData.bank_fd, FIELD_CONFIGS.bank_fd, [139, 92, 246]);
      
      // Post Office
      renderPDFSection('Post Office Schemes', vaultData.post_office, FIELD_CONFIGS.post_office, [249, 115, 22]);
      
      // Shares
      renderPDFSection('Shares', vaultData.shares, FIELD_CONFIGS.shares, [6, 182, 212]);
      
      // MF SIP
      renderPDFSection('Mutual Fund SIP', vaultData.mf_sip, FIELD_CONFIGS.mf_sip, [99, 102, 241]);
      
      // MF Lumpsum
      renderPDFSection('Mutual Fund Lumpsum', vaultData.mf_lumpsum, FIELD_CONFIGS.mf_lumpsum, [139, 92, 246]);
      
      // Loans
      renderPDFSection('Loans', vaultData.loans, FIELD_CONFIGS.loans, [244, 63, 94]);

      // Document Locations (Text Field)
      if (vaultData.document_locations) {
        checkPageBreak(40);
        pdf.setFillColor(251, 191, 36);
        pdf.roundedRect(margin, yPos, 4, 8, 1, 1, 'F');
        addText('DOCUMENT LOCATIONS', margin + 8, yPos + 6, { fontSize: 11, fontStyle: 'bold', color: [30, 58, 95] });
        addLine(yPos + 10, [200, 200, 200]);
        yPos += 15;
        
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, 25, 2, 2, 'F');
        const docText = pdf.splitTextToSize(vaultData.document_locations, pageWidth - 2 * margin - 10);
        addText(docText.slice(0, 3).join('\n'), margin + 5, yPos + 8, { fontSize: 9, color: [50, 50, 50] });
        yPos += 30;
      }

      // Will Information
      if (vaultData.will && (vaultData.will.has_will || vaultData.will.location)) {
        checkPageBreak(40);
        pdf.setFillColor(245, 158, 11);
        pdf.roundedRect(margin, yPos, 4, 8, 1, 1, 'F');
        addText('WILL INFORMATION', margin + 8, yPos + 6, { fontSize: 11, fontStyle: 'bold', color: [30, 58, 95] });
        addLine(yPos + 10, [200, 200, 200]);
        yPos += 15;
        
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, 20, 2, 2, 'F');
        addText(`Will Status: ${vaultData.will.has_will ? 'YES - Will Created' : 'No Will'}`, margin + 5, yPos + 8, { fontSize: 10, fontStyle: 'bold', color: [30, 30, 30] });
        if (vaultData.will.location) {
          addText(`Location: ${vaultData.will.location}`, margin + 5, yPos + 15, { fontSize: 9, color: [80, 80, 80] });
        }
        yPos += 25;
      }

      // Anything Else
      if (vaultData.anything_else) {
        checkPageBreak(40);
        pdf.setFillColor(107, 114, 128);
        pdf.roundedRect(margin, yPos, 4, 8, 1, 1, 'F');
        addText('ADDITIONAL NOTES', margin + 8, yPos + 6, { fontSize: 11, fontStyle: 'bold', color: [30, 58, 95] });
        addLine(yPos + 10, [200, 200, 200]);
        yPos += 15;
        
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, 25, 2, 2, 'F');
        const extraText = pdf.splitTextToSize(vaultData.anything_else, pageWidth - 2 * margin - 10);
        addText(extraText.slice(0, 3).join('\n'), margin + 5, yPos + 8, { fontSize: 9, color: [50, 50, 50] });
        yPos += 30;
      }

      // Footer on last page
      const footerY = pageHeight - 15;
      addLine(footerY - 5, [200, 200, 200]);
      addText('This document contains confidential financial information. Keep it secure.', margin, footerY, { fontSize: 8, color: [150, 150, 150] });
      addText(`Page ${pdf.internal.getNumberOfPages()}`, pageWidth - margin - 15, footerY, { fontSize: 8, color: [150, 150, 150] });

      // Save the PDF
      const fileName = `My_Family_Should_Know_${new Date().toISOString().split('T')[0]}.pdf`;

      if (mode === 'share') {
        // Return base64 without triggering download
        const base64 = pdf.output('datauristring').split(',')[1];
        return { fileName, base64 };
      }

      pdf.save(fileName);
      setMessage({ type: 'success', text: 'PDF downloaded successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to generate PDF. Please try again.' });
      return null;
    } finally {
      if (mode === 'download') setGeneratingPDF(false);
    }
  };

  const handleAddItem = async (section) => {
    setSaving(true);
    try {
      await axios.post(`${API}/client/family-vault/${section}`, formData, { headers: headers() });
      await fetchVaultData();
      setAddingTo(null);
      setFormData({});
      setMessage({ type: 'success', text: 'Item added successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add item' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (section, itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${API}/client/family-vault/${section}/${itemId}`, { headers: headers() });
      await fetchVaultData();
      setMessage({ type: 'success', text: 'Item deleted successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete item' });
    }
  };

  const handleSaveTextField = async (field, value) => {
    setSaving(true);
    try {
      const updatedVault = { ...vaultData, [field]: value };
      await axios.put(`${API}/client/family-vault`, updatedVault, { headers: headers() });
      setVaultData(updatedVault);
      setMessage({ type: 'success', text: 'Saved successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWill = async (willData) => {
    setSaving(true);
    try {
      const updatedVault = { ...vaultData, will: willData };
      await axios.put(`${API}/client/family-vault`, updatedVault, { headers: headers() });
      setVaultData(updatedVault);
      setMessage({ type: 'success', text: 'Will information saved' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save will information' });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `₹${Number(amount).toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <FaSpinner className="animate-spin text-[#FFD700] text-3xl" />
      </div>
    );
  }

  const renderFormField = (field) => {
    const value = formData[field.name] || '';
    
    if (field.type === 'select') {
      return (
        <select
          value={value}
          onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          className="w-full px-3 py-2 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] outline-none"
          required={field.required}
        >
          <option value="">Select {field.label}</option>
          {field.options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    
    return (
      <input
        type={field.type}
        value={value}
        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
        placeholder={field.label}
        className="w-full px-3 py-2 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] outline-none"
        required={field.required}
      />
    );
  };

  const renderItemCard = (section, item, fields) => (
    <div key={item.id} className="bg-[#1A2942] rounded-lg p-4 border border-[#2D5A8F]">
      <div className="flex justify-between items-start mb-3">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 flex-1">
          {fields.slice(0, 3).map(field => (
            <div key={field.name}>
              <p className="text-gray-500 text-xs">{field.label}</p>
              <p className="text-white text-sm">
                {field.type === 'number' && field.name.includes('amount') 
                  ? formatCurrency(item[field.name])
                  : field.type === 'date' 
                    ? formatDate(item[field.name])
                    : item[field.name] || '-'}
              </p>
            </div>
          ))}
        </div>
        <button
          onClick={() => handleDeleteItem(section, item.id)}
          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all"
        >
          <FaTrash size={14} />
        </button>
      </div>
      {fields.length > 3 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-3 border-t border-[#2D5A8F]">
          {fields.slice(3).map(field => (
            <div key={field.name}>
              <p className="text-gray-500 text-xs">{field.label}</p>
              <p className="text-white text-sm">
                {field.type === 'number' && (field.name.includes('amount') || field.name.includes('cover'))
                  ? formatCurrency(item[field.name])
                  : field.type === 'date' 
                    ? formatDate(item[field.name])
                    : item[field.name] || '-'}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderSection = (section) => {
    const config = SECTIONS.find(s => s.id === section.id);
    const Icon = config.icon;
    const isExpanded = expandedSection === section.id;
    const items = vaultData?.[section.id] || [];
    const fields = FIELD_CONFIGS[section.id];
    const isAddingHere = addingTo === section.id;

    // Special handling for text fields
    if (section.id === 'document_locations' || section.id === 'anything_else') {
      return (
        <div key={section.id} className="bg-[#0F172A] rounded-xl border border-[#2D5A8F] overflow-hidden">
          <button
            onClick={() => setExpandedSection(isExpanded ? null : section.id)}
            className="w-full px-4 py-4 flex items-center justify-between hover:bg-[#1A2942] transition-all"
          >
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-lg bg-${config.color}-500/20 flex items-center justify-center`}>
                <Icon className={`text-${config.color}-400`} />
              </div>
              <span className="text-white font-medium">{config.label}</span>
            </div>
            {isExpanded ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
          </button>
          
          {isExpanded && (
            <div className="px-4 pb-4">
              <textarea
                value={vaultData?.[section.id] || ''}
                onChange={(e) => setVaultData({ ...vaultData, [section.id]: e.target.value })}
                placeholder={section.id === 'document_locations' 
                  ? "Enter locations of important documents (e.g., Locker at SBI Bank, Andheri Branch; Home safe in bedroom...)"
                  : "Enter any other important information your family should know..."}
                className="w-full h-32 px-4 py-3 bg-[#1A2942] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] outline-none resize-none"
              />
              <button
                onClick={() => handleSaveTextField(section.id, vaultData?.[section.id] || '')}
                disabled={saving}
                className="mt-2 px-4 py-2 bg-[#FFD700] text-[#0A1929] rounded-lg font-medium hover:bg-[#FFA500] transition-all flex items-center space-x-2 disabled:opacity-50"
              >
                {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                <span>Save</span>
              </button>
            </div>
          )}
        </div>
      );
    }

    // Special handling for Will section
    if (section.id === 'will') {
      const willData = vaultData?.will || { has_will: false, execution_date: '', copy_location: '' };
      return (
        <div key={section.id} className="bg-[#0F172A] rounded-xl border border-[#2D5A8F] overflow-hidden">
          <button
            onClick={() => setExpandedSection(isExpanded ? null : section.id)}
            className="w-full px-4 py-4 flex items-center justify-between hover:bg-[#1A2942] transition-all"
          >
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-lg bg-${config.color}-500/20 flex items-center justify-center`}>
                <Icon className={`text-${config.color}-400`} />
              </div>
              <span className="text-white font-medium">{config.label}</span>
              {willData.has_will && (
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">Yes</span>
              )}
            </div>
            {isExpanded ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
          </button>
          
          {isExpanded && (
            <div className="px-4 pb-4 space-y-4">
              <div className="flex items-center space-x-4">
                <label className="text-gray-400">Do you have a Will?</label>
                <div className="flex space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="has_will"
                      checked={willData.has_will === true}
                      onChange={() => setVaultData({ ...vaultData, will: { ...willData, has_will: true } })}
                      className="text-[#FFD700]"
                    />
                    <span className="text-white">Yes</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="has_will"
                      checked={willData.has_will === false}
                      onChange={() => setVaultData({ ...vaultData, will: { ...willData, has_will: false } })}
                      className="text-[#FFD700]"
                    />
                    <span className="text-white">No</span>
                  </label>
                </div>
              </div>
              
              {willData.has_will && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Execution Date</label>
                    <input
                      type="date"
                      value={willData.execution_date || ''}
                      onChange={(e) => setVaultData({ ...vaultData, will: { ...willData, execution_date: e.target.value } })}
                      className="w-full px-3 py-2 bg-[#1A2942] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Copy Kept In</label>
                    <input
                      type="text"
                      value={willData.copy_location || ''}
                      onChange={(e) => setVaultData({ ...vaultData, will: { ...willData, copy_location: e.target.value } })}
                      placeholder="e.g., Bank locker, Lawyer's office"
                      className="w-full px-3 py-2 bg-[#1A2942] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] outline-none"
                    />
                  </div>
                </div>
              )}
              
              <button
                onClick={() => handleSaveWill(vaultData?.will)}
                disabled={saving}
                className="px-4 py-2 bg-[#FFD700] text-[#0A1929] rounded-lg font-medium hover:bg-[#FFA500] transition-all flex items-center space-x-2 disabled:opacity-50"
              >
                {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                <span>Save</span>
              </button>
            </div>
          )}
        </div>
      );
    }

    // Standard list sections
    return (
      <div key={section.id} className="bg-[#0F172A] rounded-xl border border-[#2D5A8F] overflow-hidden">
        <button
          onClick={() => setExpandedSection(isExpanded ? null : section.id)}
          className="w-full px-4 py-4 flex items-center justify-between hover:bg-[#1A2942] transition-all"
        >
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-lg bg-${config.color}-500/20 flex items-center justify-center`}>
              <Icon className={`text-${config.color}-400`} />
            </div>
            <span className="text-white font-medium">{config.label}</span>
            {items.length > 0 && (
              <span className="px-2 py-0.5 bg-[#FFD700]/20 text-[#FFD700] text-xs rounded-full">{items.length}</span>
            )}
          </div>
          {isExpanded ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
        </button>
        
        {isExpanded && (
          <div className="px-4 pb-4 space-y-3">
            {/* Existing Items */}
            {items.map(item => renderItemCard(section.id, item, fields))}
            
            {items.length === 0 && !isAddingHere && (
              <p className="text-gray-500 text-center py-4">No records added yet</p>
            )}
            
            {/* Add Form */}
            {isAddingHere ? (
              <div className="bg-[#1A2942] rounded-lg p-4 border border-[#FFD700]">
                <h4 className="text-white font-medium mb-3">Add New {config.label}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {fields.map(field => (
                    <div key={field.name}>
                      <label className="block text-gray-400 text-xs mb-1">
                        {field.label} {field.required && <span className="text-red-400">*</span>}
                      </label>
                      {renderFormField(field)}
                    </div>
                  ))}
                </div>
                <div className="flex space-x-2 mt-4">
                  <button
                    onClick={() => handleAddItem(section.id)}
                    disabled={saving}
                    className="px-4 py-2 bg-[#FFD700] text-[#0A1929] rounded-lg font-medium hover:bg-[#FFA500] transition-all flex items-center space-x-2 disabled:opacity-50"
                  >
                    {saving ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                    <span>Save</span>
                  </button>
                  <button
                    onClick={() => { setAddingTo(null); setFormData({}); }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => { setAddingTo(section.id); setFormData({}); }}
                className="w-full py-3 border-2 border-dashed border-[#2D5A8F] rounded-lg text-gray-400 hover:text-[#FFD700] hover:border-[#FFD700] transition-all flex items-center justify-center space-x-2"
              >
                <FaPlus />
                <span>Add {config.label}</span>
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#FFD700] to-[#FFA500] flex items-center justify-center">
              <FaShieldAlt size={24} className="text-[#0A1929]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">My Family Should Know</h2>
              <p className="text-gray-400">Secure vault for your important financial information</p>
            </div>
          </div>
          
          {/* Download PDF Button */}
          <button
            onClick={() => generatePDF('download')}
            disabled={generatingPDF || !vaultData}
            className={`flex items-center space-x-2 px-5 py-3 rounded-lg font-bold transition-all ${
              generatingPDF || !vaultData
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] hover:from-[#FFD700] hover:to-[#D4AF37] transform hover:scale-105'
            }`}
            data-testid="download-vault-pdf-btn"
          >
            {generatingPDF ? (
              <>
                <FaSpinner className="animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <FaDownload />
                <span>Download PDF</span>
              </>
            )}
          </button>

          {/* Share via Email Button */}
          <button
            onClick={() => setShowShareModal(true)}
            disabled={!vaultData}
            className={`flex items-center space-x-2 px-5 py-3 rounded-lg font-bold transition-all ${
              !vaultData
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-[#0F4C5C] text-white hover:bg-[#093039] shadow-md shadow-[#0F4C5C]/25 transform hover:scale-105'
            }`}
            data-testid="share-vault-btn"
          >
            <FaEnvelope />
            <span>Share via Email</span>
          </button>
        </div>
        
        {/* Warning Banner */}
        <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start space-x-3">
          <FaExclamationTriangle className="text-yellow-400 mt-0.5" />
          <p className="text-yellow-200 text-sm">
            This information is confidential. Store details of all your financial assets and important documents 
            so your family knows about them in case of emergency.
          </p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg ${message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
          {message.text}
          <button onClick={() => setMessage(null)} className="float-right"><FaTimes /></button>
        </div>
      )}

      {/* Sections */}
      <div className="space-y-3">
        {SECTIONS.map(section => renderSection(section))}
      </div>

      {/* Share via Email modal */}
      {showShareModal && (
        <ShareFamilyVaultModal
          generatePDF={generatePDF}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};

export default FamilyVault;
